import React, { FC, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Book } from 'react-bootstrap-icons';
import Dropzone from 'react-dropzone';

import { ITranslatedFile, ICmsArticleCountryListItem } from 'api/types';
import { FormElement, Modal, Button } from 'components/controls';

import styles from 'theme/components/controls/TranslatedFile/TranslatedFile.module.scss';

// typ danych wejściowych
interface IProps {
  name: string;
  selectedCountry: string;
  file: ITranslatedFile[];
  setFieldValue: (arg1: string, arg2: ITranslatedFile[]) => void;
  countries: ICmsArticleCountryListItem[];
}

const TranslatedInput: FC<IProps> = ({ name, file, selectedCountry, setFieldValue, countries }) => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState(file);
  const [open, setOpen] = useState(false);

  const [mimeType, setMimeType] = useState('image/png');

  useEffect(() => {
    if (file && file.length) {
      setFormValues(file);
      setActiveTab(file[0]?.language || '');
    } else if (countries.length) {
      const newValues = countries.map((country) => ({
        language: country.code,
        base64: '',
        name: ''
      }));

      setFormValues(newValues);

      // ustawianie aktywnego tabu
      setActiveTab(newValues?.[0]?.language);
    }
  }, [file, countries]);

  // lista tabów
  const tabs =
    formValues?.map((o) => ({
      key: o.language,
      label: o.language
    })) || [];

  // rodzaj aktywnej listy
  const [activeTab, setActiveTab] = useState<string>(tabs?.[0]?.key || '');

  const currentLanguageValue = useMemo(
    () => formValues?.find((o) => o.language === selectedCountry)?.language,
    [formValues, selectedCountry]
  );

  const handleTabChange = (tab: string) => {
    // ustawienie delay, żeby odmontować i zamontować komponenty
    setTimeout(() => {
      setActiveTab(tab);
    }, 10);
  };

  const base64ToBlobUrl = (base64: string) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeType });
    return URL.createObjectURL(blob);
  };

  const onChangeHandler = (file?: File, language?: string, onSave?: boolean) => {
    if (!file || !language) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      if (formValues && Array.isArray(formValues)) {
        const newValue: ITranslatedFile[] = formValues.map((o) => {
          if (o.language === language) {
            return {
              ...o,
              base64: reader.result as string,
              name: file.name,
              language: language
            };
          }
          return o;
        });
        setFormValues(newValue);

        if (onSave) {
          setFieldValue(name, newValue);
        }

        const mimeMatch = (reader.result as string).match(/^data:(.*?);base64,/);
        const mimeType = mimeMatch ? mimeMatch[1] : undefined;

        mimeType && setMimeType(mimeType);
      }
    };
  };

  const handleSave = () => {
    setOpen(false);
    formValues && setFieldValue(name, formValues);
  };

  const renderActiveComponent = () => {
    const selectedValue = formValues?.find((o) => o.language === activeTab);
    const hasImage = !!(selectedValue?.base64 || selectedValue?.url);

    return (
      <div className={styles.activeComponent}>
        <Dropzone
          onDrop={(acceptedFiles) => onChangeHandler(acceptedFiles[0], selectedValue?.language)}
          accept={{ 'image/*': [] }}
          multiple={false}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={classnames(styles.dropzone, {
                [styles.dropzoneActive]: isDragActive,
                [styles.dropzoneHasImage]: hasImage
              })}>
              <input {...getInputProps()} />
              {hasImage ? (
                <img
                  className={styles.dropzoneImage}
                  src={
                    selectedValue?.base64
                      ? base64ToBlobUrl(selectedValue.base64)
                      : selectedValue?.url
                  }
                />
              ) : (
                <div className={styles.dropzonePlaceholder}>
                  <span className={styles.dropzoneIcon}>🖼</span>
                  <span className={styles.dropzoneText}>
                    {isDragActive
                      ? t('Upuść zdjęcie tutaj')
                      : t('Przeciągnij zdjęcie lub kliknij, aby wybrać')}
                  </span>
                  <span className={styles.dropzoneHint}>PNG, JPG, WEBP, SVG</span>
                </div>
              )}
              {isDragActive && hasImage && (
                <div className={styles.dropzoneOverlay}>
                  <span>{t('Upuść zdjęcie tutaj')}</span>
                </div>
              )}
            </div>
          )}
        </Dropzone>

        {hasImage && (
          <div className={styles.dropzoneActions}>
            <label className={styles.fileButton}>
              {t('Zmień zdjęcie')}
              <input
                type="file"
                accept="image/*"
                onChange={({ target: { files } }) =>
                  onChangeHandler(files?.[0], selectedValue?.language)
                }
              />
            </label>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => {
                if (formValues && Array.isArray(formValues)) {
                  const newValue = formValues.map((o) =>
                    o.language === selectedValue?.language
                      ? { ...o, base64: '', name: '', url: undefined }
                      : o
                  );
                  setFormValues(newValue);
                }
              }}>
              {t('Usuń zdjęcie')}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={styles.content}>
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={classnames(styles.tab, {
                [styles.tabActive]: tab.key === activeTab
              })}
              onClick={() => (setActiveTab(''), handleTabChange(tab.key))}>
              {tab.label}
            </div>
          ))}
        </div>
        <div className={styles.contentWrapper}>{renderActiveComponent()}</div>
      </div>
    );
  };

  const currentEntry = formValues?.find((o) => o.language === selectedCountry);
  const hasCurrentImage = !!(currentEntry?.base64 || currentEntry?.url);

  return (
    <div className={styles.componentWrapper}>
      <FormElement>
        <Dropzone
          onDrop={(acceptedFiles) =>
            onChangeHandler(acceptedFiles[0], currentLanguageValue, true)
          }
          accept={{ 'image/*': [] }}
          multiple={false}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={classnames(styles.mainDropzone, {
                [styles.mainDropzoneActive]: isDragActive,
                [styles.mainDropzoneHasImage]: hasCurrentImage
              })}>
              <input {...getInputProps()} />

              {hasCurrentImage ? (
                <>
                  <img
                    className={styles.thumbnail}
                    src={
                      currentEntry?.base64
                        ? base64ToBlobUrl(currentEntry.base64)
                        : currentEntry?.url
                    }
                  />
                  {isDragActive && (
                    <div className={styles.dropzoneOverlay}>
                      <span>{t('Upuść, aby zmienić zdjęcie')}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.mainDropzonePlaceholder}>
                  <span className={styles.dropzoneIcon}>🖼</span>
                  <span className={styles.dropzoneText}>
                    {isDragActive
                      ? t('Upuść zdjęcie tutaj')
                      : t('Przeciągnij zdjęcie lub kliknij, aby wybrać')}
                  </span>
                  <span className={styles.dropzoneHint}>PNG, JPG, WEBP, SVG</span>
                </div>
              )}
            </div>
          )}
        </Dropzone>

        {hasCurrentImage && (
          <div className={styles.mainDropzoneFooter}>
            <span className={styles.fileName}>{currentEntry?.name || ''}</span>
            <button
              type="button"
              className={styles.removeButtonSmall}
              onClick={(e) => {
                e.stopPropagation();
                if (formValues && Array.isArray(formValues)) {
                  const newValue = formValues.map((o) =>
                    o.language === selectedCountry
                      ? { ...o, base64: '', name: '', url: undefined }
                      : o
                  );
                  setFormValues(newValue);
                  setFieldValue(name, newValue);
                }
              }}>
              {t('Usuń')}
            </button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              ghost
              color="secondary">
              <Book size="18px" />
            </Button>
          </div>
        )}

        {!hasCurrentImage && (
          <div className={styles.mainDropzoneFooter}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              ghost
              color="secondary">
              <Book size="18px" />
            </Button>
          </div>
        )}
      </FormElement>

      {open && (
        <Modal fullWidth onClose={() => setOpen(false)} title={t('Słownik')}>
          {renderContent()}
          <div className={styles.actionWrapper}>
            <Button onClick={() => handleSave()}>
              <Trans>Zapisz</Trans>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TranslatedInput;
